import { useEffect, useState } from "react";
import {
  Loader2,
  Plus,
  Save,
  Eye,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminGetExperience,
  adminUpdateExperience,
} from "@/api/client";
import { CardEditor } from "./CardEditor";

const inputCls =
  "w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50";

const TABS = [
  { key: "cards", label: "Cards" },
  { key: "settings", label: "Story Settings" },
];

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// Editor for one Story Experience: variable Cards, Completion CTAs, and Settings.
export const StoryEditor = ({ eid, previewUrl, onSaved }) => {
  const [exp, setExp] = useState(null);
  const [tab, setTab] = useState("cards");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setExp(null);
    setDirty(false);
    setTab("cards");
    adminGetExperience(eid)
      .then(setExp)
      .catch(() => toast.error("Could not load story"));
  }, [eid]);

  const patch = (p) => {
    setExp((prev) => ({ ...prev, ...p }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        option_title: exp.option_title,
        option_hook: exp.option_hook,
        estimated_time_seconds: exp.estimated_time_seconds,
        active: exp.active,
        cards: exp.cards || [],
      };
      const updated = await adminUpdateExperience(eid, payload);
      setExp(updated);
      setDirty(false);
      toast.success("Story saved");
      onSaved && onSaved(updated);
    } catch {
      toast.error("Could not save story");
    } finally {
      setSaving(false);
    }
  };

  if (!exp) {
    return (
      <div className="grid place-items-center py-20 text-amber-100/40">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const cards = exp.cards || [];

  // ---- card ops ----
  const setCard = (i, updated) =>
    patch({ cards: cards.map((c, ci) => (ci === i ? updated : c)) });
  const addCard = () =>
    patch({ cards: [...cards, { type: "STORY", title: "", body: "", show_image: false, image: "", alt: "" }] });
  const removeCard = (i) => patch({ cards: cards.filter((_, ci) => ci !== i) });
  const moveCard = (i, dir) => patch({ cards: move(cards, i, dir) });

  return (
    <div data-testid="story-editor">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <p className="text-lg font-semibold text-amber-100">
            {exp.option_title || "Untitled story"}
          </p>
          <span
            className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
              exp.active
                ? "bg-emerald-400/15 text-emerald-300"
                : "bg-amber-400/10 text-amber-300/80"
            }`}
          >
            {exp.active ? "Published" : "Draft"}
          </span>
        </div>
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3.5 py-2 text-xs font-medium text-amber-200 hover:bg-amber-400/10"
          >
            <Eye className="h-3.5 w-3.5" /> Preview
          </a>
        )}
        <button
          data-testid="story-save-btn"
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2 text-xs font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {dirty ? "Save story" : "Saved"}
        </button>
      </div>

      <div className="mb-5 flex gap-2 border-b border-amber-300/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`story-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "border-amber-400 text-amber-200"
                : "border-transparent text-amber-100/50 hover:text-amber-100/80"
            }`}
          >
            {t.label}
            {t.key === "cards" && ` (${cards.length})`}
          </button>
        ))}
      </div>

      {tab === "cards" && (
        <div className="space-y-4">
          {cards.map((card, i) => (
            <CardEditor
              key={i}
              card={card}
              index={i}
              total={cards.length}
              onChange={(u) => setCard(i, u)}
              onRemove={removeCard}
              onMove={moveCard}
            />
          ))}
          <button
            data-testid="add-card-btn"
            onClick={addCard}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-amber-300/25 py-4 text-sm font-medium text-amber-200 hover:border-amber-300/50 hover:bg-amber-400/5"
          >
            <Plus className="h-4 w-4" /> Add card
          </button>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-xl space-y-4 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-6">
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">Story title (option title)</label>
            <input data-testid="story-title-input" value={exp.option_title || ""} onChange={(e) => patch({ option_title: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">Hook (shown when a feeling has multiple stories)</label>
            <input value={exp.option_hook || ""} onChange={(e) => patch({ option_hook: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-amber-100/50">Estimated duration (seconds)</label>
            <input type="number" min="10" value={exp.estimated_time_seconds || 60} onChange={(e) => patch({ estimated_time_seconds: parseInt(e.target.value || "60", 10) })} className={`${inputCls} max-w-[140px]`} />
          </div>
          <label className="flex items-center gap-2 text-sm text-amber-100/80">
            <input
              data-testid="story-publish-toggle"
              type="checkbox"
              checked={!!exp.active}
              onChange={(e) => patch({ active: e.target.checked })}
              className="h-4 w-4 accent-amber-400"
            />
            Published (visible in the live experience)
          </label>
          {exp.complete_story_url && (
            <a href={exp.complete_story_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:underline">
              <ExternalLink className="h-3.5 w-3.5" /> Complete story link
            </a>
          )}
        </div>
      )}
    </div>
  );
};
